"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

/*
 * 몰 접속 로그 — 화면이 바뀔 때마다 BFF 에 알립니다.
 *
 * 🔴 **왜 브라우저가 신호를 보내야 하나** — 원칙은 "서버가 기록" 입니다
 *    (`docs/base/08` §2-1). 그런데 몰은 SPA 라 화면 전환이 HTTP 요청이 아닙니다.
 *    서버는 첫 진입 한 번만 보고 그다음은 아무것도 못 봅니다.
 *    그래서 화면 전환을 **JS 가 알리고, 기록은 여전히 서버가** 합니다
 *    (오너 결정 2026-09-07 #6 · `docs/context/2026-09-07-몰-결정.md`).
 *
 * 🔴 **방문자·세션 식별은 여기서 하지 않습니다.** `app_v`·`app_s` 쿠키는 BFF 가
 *    만들고 굴립니다 — httpOnly 라 이 파일에서는 읽을 수도 없습니다.
 *    브라우저가 방문자 번호를 정하면 사용자가 그 값을 바꿀 수 있습니다.
 *
 * 🔑 **실패해도 화면에 아무 일이 없어야 합니다.** 통계 때문에 쇼핑이 멈추면
 *    본말이 뒤집힙니다 — 모든 오류를 삼킵니다.
 */

async function send(payload: {
  path: string;
  query?: string;
  referer?: string;
  title?: string;
}): Promise<void> {
  try {
    await fetch("/bff/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "same-origin",
      /* 🔑 화면을 떠나는 순간에도 나가게 합니다. */
      keepalive: true,
    });
  } catch {
    /* 조용히 넘어갑니다 — 위 머리말 참조. */
  }
}

/**
 * 화면 전환 기록기. 🔴 **첫 렌더도 한 건입니다** — 홈으로 들어온 방문이
 * 기록되지 않으면 통계에서 유입이 통째로 빕니다.
 *
 * ⚠️ `useSearchParams()` 를 쓰므로 **`<Suspense>` 안에서 렌더해야 합니다.**
 *    없으면 `next build` 가 정적 생성에서 막힙니다. 셸이 감싸 두었습니다.
 */
export function PageTracker(): null {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const query = searchParams.toString();

  /* 직전에 보낸 화면. 같은 화면을 두 번 세지 않기 위한 것입니다. */
  const sentRef = useRef<string | null>(null);

  useEffect(() => {
    /*
     * 🔴 **미리보기(`/preview`)는 세지 않습니다** — 인트라넷이 폼을 고칠 때마다 iframe 을
     *    새로 불러 관리자 한 명이 수십 건의 «방문» 을 만듭니다(`docs/base/11` §7-2).
     */
    if (pathname === "/preview") return;
    const here = query ? `${pathname}?${query}` : pathname;
    if (sentRef.current === here) return;

    const previous = sentRef.current;
    sentRef.current = here;

    /*
     * 🔑 앱 안에서 옮겨 왔으면 그 화면이 유입원입니다. 첫 진입이면 브라우저가
     *    아는 바깥 주소(`document.referrer`)를 씁니다 — 그것이 진짜 유입원입니다.
     */
    const referer = previous
      ? `${window.location.origin}${previous}`
      : document.referrer || undefined;

    /*
     * 🔴 **미루지 않고 바로 보냅니다.** 종전에는 `document.title` 이 화면과 같은
     *    시점에 바뀐다는 보장이 없어 300ms 기다렸는데, 서버가 `title` 을 받기만
     *    하고 **저장하지 않으므로**(계약 §3-4 · 2026-09-07 확정) 기다릴 이유가
     *    없습니다. 오히려 그 사이에 다음 화면으로 넘어가면 정리 함수가 타이머를
     *    지워 **그 화면의 방문이 통째로 빠집니다** — 빠르게 훑는 사람의 기록만
     *    사라지는, 눈에 안 띄는 손실이었습니다.
     *
     * 🔑 제목은 그대로 실어 보냅니다. 지금은 버려지지만 계약이 받는 값이고,
     *    나중에 저장하게 되면 그때 이 자리를 다시 보면 됩니다.
     */
    void send({
      path: pathname,
      query: query || undefined,
      referer,
      title: document.title || undefined,
    });
  }, [pathname, query]);

  return null;
}
